// The `paragraph` widget: a Quill editor over the delta a paragraph stores.
//
// Named by two schema fields and nothing else — `/Page/Element/Type/Paragraph`'s
// `content` and a `paragraph` param's `default` — which is the whole of the
// paragraph port's authoring side.
//
// WHY IT LIVES IN THE APP AND NOT IN @repoflow/components
//
// That is where the other five editor widgets are, and there is an orphaned
// copy of this one there already (widgets/paragraph, from the same gen-2 sweep,
// unbuildable since it arrived). It stays here anyway: @repoflow/components is
// a PATH dependency of all four apps, so a package added to it is installed by
// three projects that will never render a paragraph, and Quill is 3 MB
// unpacked. The same argument the dual pane made against react-split.
//
// WHY THE EDITOR IS LAZY, AND WHY IT IS NOT WARMED
//
// Quill plus its stylesheet is ~45 kB gzip for two fields of one screen. Loaded
// statically it lands in `main.js` and every visitor pays it to look at a list
// of sites; behind React.lazy it is a chunk.
//
// @repoflow/components/code does the same and then WARMS the chunk from module
// scope, because a cold lazy component mounts a frame late and a form that
// re-renders a frame late can lose a modal open over it (P143). Three things
// say not to copy that here:
//
//   - No screen this app has puts a paragraph field inside a modal, and the two
//     that could are configured empty on purpose: the add dialog's Paragraph
//     branch (see the elements route) and a paragraph param's `default` in
//     @repoflow/components/types-form. What is left is a shape a tenant can
//     declare rather than one a screen has — a `list` param whose items are
//     paragraphs, whose rows the list widget opens in a modal — and if that
//     turns out to be a real way to write a page, warming is the remedy and
//     this comment is where to start.
//
//     Note that the editor now OPENS a modal of its own — the file picker
//     behind the toolbar's image button. That is the other way round and is
//     safe: the modal is opened from a tree that is already mounted, so there
//     is no chunk fetch between the click and the modal. The shape P143 was
//     about is a modal open over a form that then re-renders, and the one to
//     watch for is still a paragraph INSIDE a modal, which would now mean a
//     picker inside a modal inside a modal.
//   - A warm is a fetch at app boot for every visitor, and unlike the code
//     editor — which four screens across two schemas reach — this is two fields
//     of one screen.
//   - Quill ships ESM from its `main` and so do three of its dependencies. A
//     warm is evaluated by anything that imports the widget registry, including
//     jest, whose resolver here does not read `exports` — so warming it means
//     exempting five packages from CRA's transformIgnorePatterns, which is more
//     than the warm buys.
import React from "react";
import * as Paragraph from "@common/render/paragraph";

const Editor = React.lazy(() => import("./editor"));

// Wide, like the code widgets: a rich-text editor beside a label in a
// two-column row is unusable at any window size.
export const config = { isWide: true };

// The delta's TEXT, not its HTML.
//
// View is what a list row and a read-only form show, and both are inside the
// app's own origin — where tenant markup must not be rendered (P142). The
// `html` widget beside this one makes the same choice for the same reason: it
// shows the source string rather than the page it describes.
//
// Text rather than the raw JSON because the raw JSON of a paragraph is
// unreadable, and a row that reads `{"ops":[{"insert":"Pricing\n"}]}` tells
// nobody which paragraph it is.
export const View = ({ value }) => <span>{Paragraph.text(value)}</span>;

export const Edit = (props) => (
  <React.Suspense
    fallback={
      <div className="border rounded bg-light" style={{ width: "100%", height: "22rem" }} />
    }
  >
    <Editor {...props} />
  </React.Suspense>
);
